import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'artists', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/e56119d9701c4940d688cc54fccf122e/0f13bebb41f5fe958e11d6d338ab3eba.png" 
              alt="Musicv2 Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Musicv2
            </span>
          </div>
          
          <div className="flex items-center gap-8">
            {['home', 'about', 'artists', 'contact'].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className={`text-sm font-medium transition-all duration-300 capitalize whitespace-nowrap cursor-pointer ${
                  activeSection === section
                    ? 'text-cyan-400'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {section}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-black to-cyan-900/40"></div>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/cf395f20efe288473227a4a6703c265a.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
                    THE FUTURE
                  </span>
                  <br />
                  <span className="text-white">OF MUSIC IS HERE</span>
                </h1>
                <p className="text-xl text-white/80 leading-relaxed">
                  AI-Powered Discovery. Global Distribution. Limitless Creativity.
                </p>
              </div>
              
              <div className="flex flex-col gap-4 w-fit">
                <button 
                  onClick={() => scrollToSection('artists')}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Discover Artists
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 bg-transparent border-2 border-cyan-400 text-cyan-400 font-semibold rounded-full hover:bg-cyan-400/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Submit Your Music
                </button>
              </div>
            </div>
            
            <div className="relative h-96 lg:h-[500px]">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative h-full flex items-center justify-center">
                <div className="w-64 h-64 bg-gradient-to-br from-cyan-400 to-purple-600 rounded-full animate-pulse opacity-20"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-music-2-line text-9xl text-cyan-400 opacity-80"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-black via-purple-950/20 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">WHAT WE DO</p>
            <h2 className="text-5xl font-bold mb-4">
              Redefining Music{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Distribution
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:row-span-2 bg-gradient-to-br from-blue-900/40 to-blue-950/40 rounded-3xl p-8 backdrop-blur-sm border border-blue-500/20 hover:border-cyan-400/50 transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer group">
              <div className="h-64 mb-6 flex items-center justify-center bg-gradient-to-br from-cyan-500/10 to-purple-600/10 rounded-2xl">
                <i className="ri-brain-line text-7xl text-cyan-400 group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">AI Talent Scouting</h3>
              <p className="text-white/70 leading-relaxed">
                Our advanced AI algorithms analyze millions of tracks to discover the next generation of musical talent, identifying unique sounds and potential hits before they go mainstream.
              </p>
            </div>

            <div className="lg:col-span-2 bg-gradient-to-br from-purple-900/40 to-purple-950/40 rounded-3xl p-8 backdrop-blur-sm border border-purple-500/20 hover:border-purple-400/50 transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer group">
              <div className="h-48 mb-6 flex items-center justify-center bg-gradient-to-br from-purple-500/10 to-pink-600/10 rounded-2xl">
                <i className="ri-global-line text-7xl text-purple-400 group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Global Digital Distribution</h3>
              <p className="text-white/70 leading-relaxed">
                Reach every major streaming platform worldwide with our seamless distribution network. From Spotify to Apple Music, we ensure your music is heard everywhere.
              </p>
            </div>

            <div className="lg:col-span-2 bg-gradient-to-br from-cyan-900/40 to-cyan-950/40 rounded-3xl p-8 backdrop-blur-sm border border-cyan-500/20 hover:border-cyan-400/50 transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer group">
              <div className="h-48 mb-6 flex items-center justify-center bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-2xl">
                <i className="ri-vip-crown-line text-7xl text-cyan-400 group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Immersive Brand Experiences</h3>
              <p className="text-white/70 leading-relaxed">
                Create unforgettable connections with fans through VR concerts, AR merchandise, and interactive digital experiences that transcend traditional music marketing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-6">OUR MISSION</p>
            <div className="max-w-4xl ml-auto">
              <p className="text-4xl lg:text-5xl font-light leading-relaxed text-white/90">
                At Musicv2, we're democratizing the music industry through cutting-edge technology. 
                We believe every artist deserves a fair shot at success, powered by AI-driven insights 
                and global reach that was once reserved for major labels.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-20">
            <div className="text-center">
              <div className="text-6xl lg:text-7xl font-bold bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent mb-3">
                500+
              </div>
              <p className="text-white/60 text-lg">Artists Signed</p>
            </div>
            <div className="text-center">
              <div className="text-6xl lg:text-7xl font-bold text-white mb-3">
                50M+
              </div>
              <p className="text-white/60 text-lg">Monthly Streams</p>
            </div>
            <div className="text-center">
              <div className="text-6xl lg:text-7xl font-bold text-cyan-400 mb-3">
                98%
              </div>
              <p className="text-white/60 text-lg">Artist Satisfaction</p>
            </div>
            <div className="text-center">
              <div className="text-6xl lg:text-7xl font-bold text-white mb-3">
                24/7
              </div>
              <p className="text-white/60 text-lg">Global Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Artists Section */}
      <section id="artists" className="py-24 bg-gradient-to-b from-gray-900 via-purple-950/10 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <i className="ri-soundwave-line text-4xl text-cyan-400"></i>
            </div>
            <p className="text-white/60 text-sm uppercase tracking-wider mb-4">ARTIST VOICES</p>
            <h2 className="text-5xl font-bold mb-4 text-white">
              Trusted by Tomorrow's Legends
            </h2>
            <p className="text-xl text-white/60">Real stories from artists breaking boundaries</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                quote: "Musicv2 transformed my career overnight",
                text: "The AI scouting found me when I was just uploading demos to SoundCloud. Now I'm charting globally and living my dream.",
                name: "Luna Rivers",
                role: "Electronic Pop Artist",
                avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20female%20electronic%20pop%20music%20artist%20with%20colorful%20hair%20in%20modern%20recording%20studio%20with%20neon%20lights%20confident%20expression%20simple%20clean%20background&width=200&height=200&seq=artist001&orientation=squarish"
              },
              {
                quote: "The distribution network is unmatched",
                text: "Within 48 hours, my single was live on every major platform worldwide. The analytics dashboard helps me understand my audience like never before.",
                name: "Marcus Chen",
                role: "Hip-Hop Producer",
                avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20male%20hip%20hop%20producer%20wearing%20headphones%20in%20modern%20studio%20with%20equipment%20confident%20smile%20simple%20clean%20background&width=200&height=200&seq=artist002&orientation=squarish"
              },
              {
                quote: "Finally, a label that gets it",
                text: "They don't just distribute music—they build brands. The immersive experiences they created for my album launch were mind-blowing.",
                name: "Aria Santos",
                role: "Indie R&B Singer",
                avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20female%20indie%20rnb%20singer%20with%20natural%20beauty%20in%20modern%20minimalist%20studio%20warm%20lighting%20simple%20clean%20background&width=200&height=200&seq=artist003&orientation=squarish"
              },
              {
                quote: "Best decision I ever made",
                text: "The team at Musicv2 treats you like family. They're invested in your success and provide support at every step of the journey.",
                name: "DJ Nexus",
                role: "EDM Producer",
                avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20male%20edm%20dj%20producer%20with%20stylish%20look%20in%20modern%20club%20setting%20with%20colorful%20lights%20confident%20pose%20simple%20clean%20background&width=200&height=200&seq=artist004&orientation=squarish"
              },
              {
                quote: "Revolutionary approach to music",
                text: "The AI insights helped me refine my sound and connect with fans who truly appreciate my art. My streams have increased 10x in just 6 months.",
                name: "Zara Knight",
                role: "Alternative Rock",
                avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20female%20alternative%20rock%20musician%20with%20edgy%20style%20holding%20guitar%20in%20modern%20studio%20confident%20expression%20simple%20clean%20background&width=200&height=200&seq=artist005&orientation=squarish"
              },
              {
                quote: "The future of independent music",
                text: "Musicv2 gives independent artists the tools and reach that were once only available to major label acts. It's truly democratizing the industry.",
                name: "Phoenix Ray",
                role: "Singer-Songwriter",
                avatar: "https://readdy.ai/api/search-image?query=professional%20portrait%20of%20young%20male%20singer%20songwriter%20with%20acoustic%20guitar%20in%20cozy%20modern%20studio%20warm%20natural%20lighting%20simple%20clean%20background&width=200&height=200&seq=artist006&orientation=squarish"
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-cyan-400/50 transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-xl text-cyan-400"></i>
                  ))}
                </div>
                <h4 className="text-xl font-bold mb-3 text-white">{testimonial.quote}</h4>
                <p className="text-white/70 leading-relaxed mb-6">{testimonial.text}</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-cyan-400">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-white/60">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-purple-900/40 via-black to-cyan-900/40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-6xl lg:text-7xl font-bold mb-6 text-white">
            Ready to Launch Your Career?
          </h2>
          <p className="text-2xl text-white/70 mb-12">
            Join 500+ artists already transforming the industry
          </p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="px-12 py-5 bg-gradient-to-r from-cyan-500 to-purple-600 text-white text-lg font-semibold rounded-full hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 inline-flex items-center gap-3 whitespace-nowrap cursor-pointer group"
          >
            Submit Your Demo
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4 text-white">Get In Touch</h2>
            <p className="text-xl text-white/60">Let's create the future of music together</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div>
                <h3 className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">CONTACT</h3>
                <p className="text-white/80 text-lg leading-relaxed">
                  123 Music Avenue, Suite 500<br />
                  Los Angeles, CA 90028
                </p>
              </div>
              
              <div>
                <h3 className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">PHONE</h3>
                <p className="text-white/80 text-lg">+1 (555) 123-4567</p>
              </div>
              
              <div>
                <h3 className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">EMAIL</h3>
                <a href="mailto:hello@musicv2.com" className="text-white/80 text-lg hover:text-cyan-400 transition-colors duration-300 cursor-pointer">
                  hello@musicv2.com
                </a>
              </div>

              <div>
                <h3 className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">FOLLOW US</h3>
                <div className="flex gap-4">
                  {[
                    { icon: 'ri-instagram-line', link: '#' },
                    { icon: 'ri-twitter-x-line', link: '#' },
                    { icon: 'ri-tiktok-line', link: '#' },
                    { icon: 'ri-spotify-line', link: '#' }
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      className="w-12 h-12 rounded-full border-2 border-cyan-400 flex items-center justify-center text-cyan-400 hover:bg-cyan-400 hover:text-black transition-all duration-300 cursor-pointer"
                    >
                      <i className={`${social.icon} text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-cyan-900/20 rounded-3xl p-8 border border-white/10">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                // Validate textarea length
                const message = formData.get('message') as string;
                if (message && message.length > 500) {
                  alert('Message must be 500 characters or less');
                  return;
                }
                
                // Get selected dropdown value
                const artistType = form.querySelector('select[name="artist-type"]') as HTMLSelectElement;
                const selectedText = artistType.options[artistType.selectedIndex].text;
                formData.set('artist-type', selectedText);
                
                try {
                  const response = await fetch('https://readdy.ai/api/form/d586ltnahj0hkf71i3fg', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  });
                  
                  if (response.ok) {
                    // Show success message
                    const successDiv = document.createElement('div');
                    successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
                    successDiv.textContent = 'Message sent successfully!';
                    document.body.appendChild(successDiv);
                    setTimeout(() => successDiv.remove(), 3000);
                    form.reset();
                  } else {
                    throw new Error('Submission failed');
                  }
                } catch (error) {
                  // Show error message
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
                  errorDiv.textContent = 'Failed to send message. Please try again.';
                  document.body.appendChild(errorDiv);
                  setTimeout(() => errorDiv.remove(), 3000);
                }
              }}>
                <div>
                  <label htmlFor="name" className="block text-white/80 text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-cyan-400 transition-colors duration-300"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-white/80 text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-cyan-400 transition-colors duration-300"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="artist-type" className="block text-white/80 text-sm font-medium mb-2">Artist Type</label>
                  <select
                    id="artist-type"
                    name="artist-type"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:border-cyan-400 transition-colors duration-300 cursor-pointer"
                  >
                    <option value="" className="bg-gray-900">Select your genre</option>
                    <option value="electronic" className="bg-gray-900">Electronic/EDM</option>
                    <option value="hiphop" className="bg-gray-900">Hip-Hop/Rap</option>
                    <option value="pop" className="bg-gray-900">Pop</option>
                    <option value="rock" className="bg-gray-900">Rock/Alternative</option>
                    <option value="rnb" className="bg-gray-900">R&B/Soul</option>
                    <option value="indie" className="bg-gray-900">Indie</option>
                    <option value="other" className="bg-gray-900">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-white/80 text-sm font-medium mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-cyan-400 transition-colors duration-300 resize-none"
                    placeholder="Tell us about your music..."
                  ></textarea>
                  <p className="text-white/40 text-xs mt-1">Maximum 500 characters</p>
                </div>
                
                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://static.readdy.ai/image/e56119d9701c4940d688cc54fccf122e/0f13bebb41f5fe958e11d6d338ab3eba.png" 
                alt="Musicv2 Logo" 
                className="h-16 w-16 object-contain"
              />
              <span className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                Musicv2
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <p className="text-white/70 leading-relaxed mb-6">
                Building the future of music through AI-driven talent discovery, 
                global distribution, and immersive brand experiences. 
                Empowering artists to reach their full potential.
              </p>
            </div>
            
            <div>
              <h4 className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">Quick Links</h4>
              <div className="space-y-3">
                {['home', 'about', 'artists', 'contact'].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className="block text-white/70 hover:text-cyan-400 transition-colors duration-300 capitalize cursor-pointer"
                  >
                    {section}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-4">Legal</h4>
              <div className="space-y-3">
                <a href="#" className="block text-white/70 hover:text-cyan-400 transition-colors duration-300 cursor-pointer">Privacy Policy</a>
                <a href="#" className="block text-white/70 hover:text-cyan-400 transition-colors duration-300 cursor-pointer">Terms of Service</a>
                <a href="#" className="block text-white/70 hover:text-cyan-400 transition-colors duration-300 cursor-pointer">Artist Agreement</a>
              </div>
            </div>
          </div>

          <div className="border-t border-cyan-400/30 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © 2025 Musicv2. All rights reserved.
            </p>
            <div className="flex gap-8">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/50 text-sm hover:text-cyan-400 transition-colors duration-300 cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}